import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Archive,
  CheckCircle2,
  Copy,
  Filter,
  Inbox,
  KeyRound,
  Layers3,
  RadioTower,
  Search,
  Send,
  Webhook,
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/shell/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { PlatformBadge } from "@/components/badges/PlatformBadge";
import { ContentInboxCard } from "@/components/content/ContentInboxCard";
import { useAppState } from "@/state/AppStateProvider";
import { ASSET_TYPE_LABELS } from "@/lib/constants";
import { buildInboxWorkspace } from "@/lib/inboxWorkspace";
import { cn, formatDateTime, timeAgo } from "@/lib/utils";

export default function ContentInbox() {
  const {
    state,
    sendTestInboxPayload,
    rotateIngestionKey,
    promoteInboxToDraft,
    archiveInbox,
  } = useAppState();
  const [tab, setTab] = useState("all");
  const [source, setSource] = useState("all");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const workspace = useMemo(() => (
    buildInboxWorkspace({
      events: state.inbox,
      filters: { tab, source, query: q },
    })
  ), [state.inbox, tab, source, q]);

  const selectedEvent = useMemo(() => (
    workspace.filtered.find((event) => event.id === selectedId) || workspace.filtered[0] || null
  ), [workspace.filtered, selectedId]);

  const copyWebhook = () => {
    navigator.clipboard?.writeText(state.ingestion.webhook_url);
    toast.success("Webhook URL copied");
  };

  const onSendTestPayload = async () => {
    try {
      const event = await sendTestInboxPayload();
      setSelectedId(event.id);
      toast.success(`${event.title} added to inbox`);
    } catch (e) {
      toast.error(`Couldn't send test payload: ${e.message}`);
    }
  };

  const onRotateKey = async () => {
    try {
      const res = await rotateIngestionKey();
      navigator.clipboard?.writeText(res.api_key);
      toast.success("New key copied to clipboard. Paste it into your agent/n8n now — it won't be shown again.", { duration: 10000 });
    } catch (e) {
      toast.error(`Couldn't rotate key: ${e.message}`);
    }
  };

  const onPromote = (id) => {
    promoteInboxToDraft(id);
    toast.success("Created draft in Library");
  };

  const onArchive = (id) => {
    archiveInbox(id);
    toast.message("Archived");
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 pb-20" data-testid="content-inbox-page">
      <PageHeader
        eyebrow="Content Inbox"
        title="Capture raw material before it becomes content."
        description="Ideas, drafts, transcripts, research notes, comments, and webhook payloads land here first so they can be triaged into the right workflow."
        testId="inbox-page-header"
      />

      <section className="grid min-w-0 gap-6 xl:grid-cols-12">
        <div className="min-w-0 space-y-4 xl:col-span-8">
          <section className="rounded-xl border bg-card p-3 inner-highlight" style={{ borderColor: "var(--border-subtle)" }} data-testid="inbox-filters">
            <div className="flex flex-wrap items-center gap-3">
              <Tabs value={tab} onValueChange={(value) => { setTab(value); setSelectedId(null); }}>
                <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-transparent p-0">
                  {workspace.tabs.map((item) => (
                    <TabsTrigger key={item.id} value={item.id} className="gap-1.5 data-[state=active]:shadow-sm" data-testid={`inbox-tab-${item.id}`}>
                      {item.label}
                      <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">{item.count}</span>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-3 border-t pt-3" style={{ borderColor: "var(--border-subtle)" }}>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Filter className="h-3.5 w-3.5" />
                Filters
              </div>
              <Select value={source} onValueChange={(value) => { setSource(value); setSelectedId(null); }}>
                <SelectTrigger className="h-9 w-full sm:w-[180px]" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} data-testid="inbox-source-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  {workspace.sourceBreakdown.map((item) => (
                    <SelectItem key={item.id} value={item.id}>{formatSource(item.id)} ({item.count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative w-full sm:ml-auto sm:max-w-sm">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(event) => setQ(event.target.value)}
                  placeholder="Search ideas, origins, or platforms..."
                  className="h-9 pl-8"
                  data-testid="inbox-search"
                  style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
                />
              </div>
            </div>
          </section>

          {workspace.filtered.length === 0 ? (
            (state.inbox || []).length === 0 ? (
              <EmptyState icon={Inbox} title="Your inbox is empty" description="Captured ideas, mentions, and webhook payloads land here for triage. Send a test payload to see it in action." />
            ) : (
              <EmptyState icon={Inbox} title="No intake items match" description="Send a test webhook or clear the filters." />
            )
          ) : (
            <section data-testid="inbox-list">
              <div className="mb-3 flex items-baseline gap-2 px-1">
                <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  {workspace.filtered.length} {workspace.filtered.length === 1 ? "item" : "items"} in view
                </p>
                <span aria-hidden className="text-muted-foreground/50">·</span>
                <p className="text-[12px] text-muted-foreground">Open an item to inspect payload context before converting it.</p>
              </div>
              <div className="grid gap-3">
                {workspace.filtered.map((event) => (
                  <ContentInboxCard
                    key={event.id}
                    event={event}
                    selected={selectedEvent?.id === event.id}
                    onOpen={() => setSelectedId(event.id)}
                    onPromote={() => onPromote(event.id)}
                    onArchive={() => onArchive(event.id)}
                  />
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="min-w-0 space-y-4 xl:col-span-4 xl:sticky xl:top-20 xl:self-start">
          <SelectedIntakePanel
            event={selectedEvent}
            onPromote={selectedEvent ? () => onPromote(selectedEvent.id) : undefined}
            onArchive={selectedEvent ? () => onArchive(selectedEvent.id) : undefined}
          />
          <IngestionEndpointPanel
            ingestion={state.ingestion}
            onCopyWebhook={copyWebhook}
            onRotateKey={onRotateKey}
            onSendTestPayload={onSendTestPayload}
          />
        </aside>
      </section>
    </motion.div>
  );
}

function SelectedIntakePanel({ event, onPromote, onArchive }) {
  if (!event) {
    return (
      <section className="rounded-xl border bg-card p-5 text-center inner-highlight" style={{ borderColor: "var(--border-subtle)" }} data-testid="inbox-selected-empty">
        <Inbox className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
        <p className="text-sm font-semibold text-1">No item selected</p>
        <p className="mt-1 text-xs text-muted-foreground">Choose an intake item to inspect the source payload.</p>
      </section>
    );
  }

  return (
    <section className="min-w-0 rounded-xl border bg-card p-4 inner-highlight" style={{ borderColor: "var(--border-subtle)" }} data-testid="inbox-selected-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Selected intake</p>
          <h2 className="mt-1 text-base font-semibold tracking-[-0.01em] text-1">{event.title}</h2>
        </div>
        <StatusBadge status={event.status} />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <SourcePill source={event.source} />
        <PlatformBadge platform={event.platform} compact />
        <span className="rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
          {ASSET_TYPE_LABELS[event.asset_type] || event.asset_type}
        </span>
      </div>

      <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{event.preview}</p>

      <div className="mt-4 rounded-lg border bg-[color:var(--surface-1)] p-3" style={{ borderColor: "var(--border-subtle)" }}>
        <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Recommended next move</p>
        <p className="mt-1 text-sm font-semibold text-1">{event.inboxAction?.label}</p>
        <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">{event.inboxAction?.hint}</p>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-[12px]">
        <MiniMeta label="Received" value={timeAgo(event.received_at)} />
        <MiniMeta label="Origin" value={event.payload?.origin || "Unknown"} />
      </div>

      <pre className="mt-4 max-h-[220px] min-w-0 overflow-auto whitespace-pre-wrap break-words rounded-lg border bg-[#0C0F0E] p-3 font-mono text-[11px] leading-[1.6] text-slate-100 scrollbar-thin" style={{ borderColor: "rgba(18, 22, 20,0.86)" }}>
{JSON.stringify(event.payload || {}, null, 2)}
      </pre>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" onClick={onPromote} className="gap-1.5" data-testid="inbox-selected-promote" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
          <CheckCircle2 className="h-3.5 w-3.5" /> {event.inboxAction?.label || "Create draft"}
        </Button>
        <Button size="sm" variant="outline" onClick={onArchive} className="gap-1.5" data-testid="inbox-selected-archive">
          <Archive className="h-3.5 w-3.5" /> Archive
        </Button>
      </div>
    </section>
  );
}

function IngestionEndpointPanel({ ingestion, onCopyWebhook, onRotateKey, onSendTestPayload }) {
  return (
    <section
      data-testid="ingestion-endpoint-panel"
      className="min-w-0 rounded-xl border bg-card p-4 inner-highlight"
      style={{ borderColor: "var(--border-subtle)" }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Webhook className="h-4 w-4 text-[color:var(--accent-cyan)]" />
            <h2 className="text-sm font-semibold tracking-[-0.005em] text-1">Ingestion endpoint</h2>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Agents POST here with <code className="rounded bg-[color:var(--surface-3)] px-1 py-0.5 text-[11px]">Authorization: Bearer &lt;key&gt;</code>. See docs/content-inbox.md.</p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]" style={{ backgroundColor: "rgba(22,163,74,0.10)", borderColor: "rgba(22,163,74,0.24)", color: "#15803D" }}>
          <KeyRound className="h-3 w-3" /> Active
        </span>
      </div>

      <div className="mt-4 space-y-3">
        <EndpointField label="Webhook URL" value={ingestion.webhook_url} action={<Button size="sm" variant="outline" onClick={onCopyWebhook} className="gap-1.5" data-testid="copy-webhook-url"><Copy className="h-3 w-3" /> Copy</Button>} />
        <EndpointField label="API key" value={ingestion.api_key_masked} action={<Button size="sm" variant="outline" onClick={onRotateKey} data-testid="rotate-webhook-key">Rotate</Button>} />
      </div>

      <div className="mt-4 rounded-lg border bg-[color:var(--surface-1)] p-3" style={{ borderColor: "var(--border-subtle)" }}>
        <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Last received</p>
        <p className="mt-1 text-sm font-medium text-1">{formatDateTime(ingestion.last_received_at)}</p>
      </div>

      <Button onClick={onSendTestPayload} className="mt-4 w-full gap-1.5" data-testid="inbox-send-test" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
        <Send className="h-4 w-4" /> Send test webhook
      </Button>
    </section>
  );
}

function EndpointField({ label, value, action }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
      <div className="mt-1.5 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded border px-2 py-1.5 font-mono text-[12px] text-foreground/85" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
          {value}
        </code>
        {action}
      </div>
    </div>
  );
}

function MiniMeta({ label, value }) {
  return (
    <div className="rounded-lg border bg-[color:var(--surface-1)] p-2" style={{ borderColor: "var(--border-subtle)" }}>
      <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-[12px] font-medium text-1">{value}</p>
    </div>
  );
}

export function SourcePill({ source }) {
  const cfg = SOURCE_TINT[source] || SOURCE_TINT.webhook;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.08em]"
      style={{ backgroundColor: cfg.bg, borderColor: cfg.border, color: cfg.text }}
      data-testid="content-inbox-source"
    >
      {formatSource(source)}
    </span>
  );
}

export function formatSource(source = "") {
  return String(source)
    .replace("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export const SOURCE_TINT = {
  claude: { bg: "rgba(217,119,6,0.10)", border: "rgba(217,119,6,0.24)", text: "#B45309" },
  codex: { bg: "rgba(22,163,74,0.10)", border: "rgba(22,163,74,0.24)", text: "#15803D" },
  openclaw: { bg: "rgba(143, 224, 74,0.10)", border: "rgba(143, 224, 74,0.24)", text: "#0B7C42" },
  hermes: { bg: "rgba(18, 147, 79,0.08)", border: "rgba(18, 147, 79,0.22)", text: "#0B7C42" },
  n8n: { bg: "rgba(220,38,38,0.10)", border: "rgba(220,38,38,0.24)", text: "#B91C1C" },
  webhook: { bg: "rgba(100,116,139,0.10)", border: "rgba(100,116,139,0.22)", text: "#475569" },
  mcp_server: { bg: "rgba(18, 147, 79,0.10)", border: "rgba(18, 147, 79,0.24)", text: "#0B7C42" },
};
